import { useSyncExternalStore } from 'react';
import type { Location } from 'react-router-dom';

/**
 * 화면 탭 목록(게시판 #168, 10-09) — 레거시 버스인 MDI 탭처럼 **열어 본 화면은 닫기 전까지 살아 있다**.
 *
 * 껍데기(`App.tsx Shell`)는 배지 때문에 자주 다시 그려진다(issues 154) — 탭 목록을 거기 state 로 두면
 * 본문(`memo(Pages)`)에 props 로 넘겨야 해 memo 가 풀린다. 그래서 바깥 저장소로 두고 필요한 곳만 구독한다.
 *
 * - 탭 순서 = 처음 연 순서(레거시처럼 오른쪽에 붙는다) · 정리 순서 = 마지막으로 본 순서.
 * - 최대 MAX 개 — 넘으면 **가장 오래 안 본 탭**부터 닫는다(숨은 화면도 메모리·DOM 을 쥔다: 출납일보 3,000줄 등).
 * - 탭을 닫으면 그 화면은 내려간다 = 다음에 열면 처음부터(입력·조건 초기화). 「새로 열기」가 필요하면 닫았다 연다.
 * - 스크롤은 창 하나를 같이 쓰므로 탭마다 위치를 적어 두고 돌아올 때 되돌린다.
 */
export type PageTab = { key: string; loc: Location; seen: number };

const MAX = 10;
let tabs: PageTab[] = [];
/**
 * ★ 즐겨찾기 탭(게시판 #222 정여진) — 껐다 켜도 열려 있고 **맨 왼쪽에 고정**, 그 뒤로 나머지 탭이 연 순서대로.
 * 계정 설정 `page.favs`(키 배열)에 남는다(다른 PC 에서도 같다). 별표 탭은 MAX 정리에서 빠지고 × 가 없다(별을 끄면 닫을 수 있다).
 */
let favs: string[] = [];
let persistFavs: ((keys: string[]) => void) | null = null;

/** 주소 키 → 처음 열 주소(회계 세부 탭은 `?tab=`). 별표 탭을 아직 안 본 채로 띄울 때 쓴다. */
function locOfKey(key: string): Location {
  const sub = subOfKey(key);
  return { pathname: pathOfKey(key), search: sub ? `?tab=${sub}` : '', hash: '', state: null, key: `fav-${key}` } as Location;
}

/** 별표 탭을 맨 앞(별표 순서), 나머지는 지금 순서 그대로. */
function pinFavs() {
  const fav = favs.map((k) => tabs.find((t) => t.key === k)).filter((t): t is PageTab => !!t);
  tabs = [...fav, ...tabs.filter((t) => !favs.includes(t.key))];
}

/** 로그인 직후 한 번 — 저장된 별표를 읽어 그 탭들을 띄운다. `save` 는 별표가 바뀔 때 계정 설정에 쓴다. */
export function initFavs(keys: unknown, save: (keys: string[]) => void): void {
  persistFavs = save;
  favs = Array.isArray(keys) ? keys.filter((k): k is string => typeof k === 'string' && k.startsWith('/')).slice(0, MAX) : [];
  for (const k of favs) if (!tabs.some((t) => t.key === k)) tabs = [...tabs, { key: k, loc: locOfKey(k), seen: 0 }];
  pinFavs();
  emit();
}

export function isFav(key: string): boolean {
  return favs.includes(key);
}

/** 별표 켜기·끄기. 켜면 별표 줄 맨 끝(오른쪽)에 붙는다. */
export function toggleFav(key: string): void {
  favs = favs.includes(key) ? favs.filter((k) => k !== key) : [...favs, key].slice(0, MAX);
  pinFavs();
  emit();
  persistFavs?.(favs);
}
let tick = 0;
const scrolls = new Map<string, number>();
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

/** 지금 보는 화면을 탭에 올린다(이미 있으면 주소·최근 순서만 갱신). */
export function touchTab(key: string, loc: Location): void {
  const at = tabs.findIndex((t) => t.key === key);
  const t = tabs[at];
  if (t) {
    if (t.loc === loc && t.seen === tick) return;
    tabs = tabs.map((x, i) => (i === at ? { key, loc, seen: ++tick } : x));
  } else {
    tabs = [...tabs, { key, loc, seen: ++tick }];
    // 별표 탭은 정리 대상이 아니다 — 그만큼은 MAX 위로 더 열린다.
    while (tabs.filter((t) => !favs.includes(t.key)).length > MAX) {
      const oldest = tabs.filter((t) => t.key !== key && !favs.includes(t.key)).reduce((a, b) => (a.seen <= b.seen ? a : b));
      tabs = tabs.filter((t) => t !== oldest);
      scrolls.delete(oldest.key);
    }
  }
  emit();
}

/** 탭을 닫는다. 닫은 게 지금 화면이면 갈 곳(가장 최근에 본 다른 탭)을 돌려준다 — 없으면 null. */
export function closeTab(key: string): PageTab | null {
  tabs = tabs.filter((t) => t.key !== key);
  scrolls.delete(key);
  emit();
  return tabs.reduce<PageTab | null>((a, b) => (!a || b.seen > a.seen ? b : a), null);
}

/** 탭 순서 바꾸기(게시판 #211 정여진 — 끌어서 옮기기). `from` 을 `to` 앞(`after` 면 뒤)으로. 최근 본 순서는 그대로. */
export function moveTab(from: string, to: string, after: boolean): void {
  if (from === to) return;
  const moving = tabs.find((t) => t.key === from);
  if (!moving || !tabs.some((t) => t.key === to)) return;
  const rest = tabs.filter((t) => t.key !== from);
  const at = rest.findIndex((t) => t.key === to);
  rest.splice(after ? at + 1 : at, 0, moving);
  tabs = rest;
  // 별표끼리 옮겼으면 그 순서를 남긴다. 별표 줄 밖으로 끌어도 별표는 맨 앞에 다시 모인다.
  const order = tabs.filter((t) => favs.includes(t.key)).map((t) => t.key);
  const changed = order.join('|') !== favs.join('|');
  favs = order;
  pinFavs();
  emit();
  if (changed) persistFavs?.(favs);
}

/** 로그아웃 — 다음 사람이 앞사람 화면을 물고 시작하지 않게. */
export function clearTabs(): void {
  tabs = [];
  favs = [];
  persistFavs = null;
  scrolls.clear();
  emit();
}

export function saveScroll(key: string, y: number): void {
  scrolls.set(key, y);
}
export function savedScroll(key: string): number {
  return scrolls.get(key) ?? 0;
}

/** 열린 화면 탭 수 — 멈춤 기록(`perfWatch.ts`)이 「탭을 많이 열면 느려지나」를 가르려고 같이 보낸다. */
export function openTabCount(): number {
  return tabs.length;
}

export function usePageTabs(): PageTab[] {
  return useSyncExternalStore(
    (f) => { subs.add(f); return () => { subs.delete(f); }; },
    () => tabs,
  );
}

/**
 * 회계는 세부 탭마다 따로 화면 탭(게시판 #211 최여진 — 「회계 › 발행 요청 / 회계 › 출납일보」 각각).
 * 화면 탭의 키 = 주소 경로인데 회계만 `?tab=` 까지 붙인다. 경로는 `pathOfKey` 로 되돌린다.
 */
export const ACCT_PATH = '/accounting';
export const acctKey = (sub: string) => `${ACCT_PATH}:${sub}`;
export const pathOfKey = (key: string) => key.split(':')[0]!;
export const acctSubOfKey = (key: string) => (key.startsWith(`${ACCT_PATH}:`) ? key.slice(ACCT_PATH.length + 1) : null);

/** 접수·배차도 세부 탭(접수·노선·입찰)마다 따로 화면 탭(2026-10-19 way 「노선 탭이 따로 안 떠 불편」). */
export const DISPATCH_PATH = '/dispatch';
export const dispatchKey = (sub: string) => `${DISPATCH_PATH}:${sub}`;
export const dispatchSubOfKey = (key: string) => (key.startsWith(`${DISPATCH_PATH}:`) ? key.slice(DISPATCH_PATH.length + 1) : null);
/** 기준정보도 세부 탭(소속·거래처·차량·차주·기사·편성·차고지)마다 따로 화면 탭(2026-10-27 way 「서로 크로스로 왔다갔다 작업하도록」). */
export const MASTER_PATH = '/master';
export const masterKey = (sub: string) => `${MASTER_PATH}:${sub}`;
export const masterSubOfKey = (key: string) => (key.startsWith(`${MASTER_PATH}:`) ? key.slice(MASTER_PATH.length + 1) : null);
/** 경영 분석도 세부 탭(월 수익·거래처별·…·추세)마다 따로 화면 탭(2026-10-27 way 「하위메뉴도 별도의 창으로 · 각 메뉴의 주소도」). */
export const PROFIT_PATH = '/profit';
export const profitKey = (sub: string) => `${PROFIT_PATH}:${sub}`;
export const profitSubOfKey = (key: string) => (key.startsWith(`${PROFIT_PATH}:`) ? key.slice(PROFIT_PATH.length + 1) : null);
/** 세부 탭 키(`/경로:sub`)면 sub — 처음 열 주소를 `?tab=sub` 로 만든다. */
const subOfKey = (key: string) => (key.includes(':') ? key.slice(key.indexOf(':') + 1) : null);

/**
 * 새로고침해도 열려 있던 화면 탭·순서 그대로(2026-10-19 way). 브라우저 창(탭)마다 — sessionStorage.
 * 되살린 탭은 **아직 안 본 상태(seen 0)** 라 누를 때 화면을 연다(새로고침 한 번에 탭 10개 조회가 몰리지 않게).
 * 저장 실패(사생활 보호 창 등)는 조용히 넘긴다 — 기억 못 해도 화면은 돈다.
 */
const SESSION_KEY = 'businweb.pageTabs';
function saveSession() {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(tabs.map((t) => ({ k: t.key, p: t.loc.pathname, s: t.loc.search }))));
  } catch { /* 기억 못 해도 된다 */ }
}
function loadSession() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '[]') as unknown;
    if (!Array.isArray(raw)) return;
    tabs = raw
      .filter((x): x is { k: string; p: string; s: string } =>
        !!x && typeof x.k === 'string' && x.k.startsWith('/') && typeof x.p === 'string' && typeof x.s === 'string')
      .slice(0, MAX * 2)
      .map((x) => ({ key: x.k, loc: { pathname: x.p, search: x.s, hash: '', state: null, key: `ss-${x.k}` } as Location, seen: 0 }));
  } catch { /* 없으면 빈 목록 */ }
}
loadSession();
subs.add(saveSession);
